import { Fragment, useEffect, useState } from 'react';
import type React from 'react';
import {
  AppPropertiesNames,
  AppPropertiesNotUsed,
  FIELD_CONFIGURATION,
  TITLE_APP_PROPERTY,
  TITLE_OBJECT_TYPE_GROUP,
  TITLE_REQUIRED_CONFIGURATION,
} from 'constants/Mapping.constants';
import _ from 'lodash';
import { parsedCustomerSignatureSelector } from 'slices/auth.slice';
import { fetchFields, fieldsSelector } from 'slices/field.slice';
import {
  requestObjectTypes,
  selectObjectTypesList,
} from 'slices/objectTypes.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TObjectType } from 'types/state.interface';
import CONSTANTS from '@timeedit/types/lib/constants';
import { Icon, Tag } from '@timeedit/ui-components';
import {
  Button,
  Card,
  Tabs,
  Tooltip,
  Typography,
} from '@timeedit/ui-components/antd';
import {
  type ValidationMapping,
  type ValidationMappingConfig,
  validateMapping,
} from './mappingValidation.helpers';
import './mapping-validation.scss';

interface Props {
  objectTypes: TObjectType[];
}

export function MappingValidation({ objectTypes }: Props) {
  const dispatch = useAppDispatch();
  const [validation, setValidation] = useState<ValidationMapping>();
  const allTypes = useAppSelector(selectObjectTypesList);
  const allFields = useAppSelector(fieldsSelector);
  const customerSignature = useAppSelector(parsedCustomerSignatureSelector);

  // Redo validation when objectTypes change. The button is there because it
  // is hard to know when objectTypes are done loading, so it's easier to wait
  // for user action, but after that we can keep the validation up to date.
  useEffect(() => {
    dispatch(requestObjectTypes());
    dispatch(fetchFields());
    if (validation !== undefined) {
      setValidation(validateMapping(objectTypes, allFields));
    }
  }, [objectTypes]);

  return (
    <div className="mapping-validation">
      <Button
        onClick={() => setValidation(validateMapping(objectTypes, allFields))}
      >
        Validate
      </Button>
      {renderContent()}
    </div>
  );

  function renderContent() {
    if (validation === undefined) {
      return null;
    }

    return (
      <>
        {validation?.valid ? (
          <Success>Mapping is valid.</Success>
        ) : (
          <Danger>{validation?.message}</Danger>
        )}
        <Tabs
          items={validation.apps.map((app, i) => ({
            key: `app-${i}`,
            label: app.label,
            children: (
              <>
                {groupAndPrint(app.configs)}
                <Card
                  className="mapping-tech-card"
                  title={'Technical information'}
                >
                  <p>
                    Repositories:{' '}
                    {app.repos.map((repo) => `timeedit/${repo}`).join(', ')}
                  </p>
                  <p>
                    AppPropertiesNotUsed: {AppPropertiesNotUsed.join(', \n')}
                  </p>
                  <p>
                    AppPropertiesNames:{' '}
                    {Object.entries(AppPropertiesNames)
                      .map(
                        (value) =>
                          value[0] +
                          '(' +
                          CONSTANTS.AppPropertyProps[value[0]].label +
                          ')' +
                          ' => ' +
                          value[1]
                      )
                      .join(', \n')}
                  </p>
                </Card>
              </>
            ),
          }))}
        />
      </>
    );
  }

  function groupAndPrint(configs: ValidationMappingConfig[]) {
    const data = _.groupBy(configs, (c) => c.title);
    return Object.entries(data).map(([name, configs]) => (
      <Card
        className="mapping-card"
        size="small"
        title={name}
        key={`${name}-mappinggroup`}
      >
        {printValidation(configs)}
      </Card>
    ));
  }

  function presentPropKey(propKey: string) {
    if (propKey === 'objectTypeGroup') return TITLE_OBJECT_TYPE_GROUP;
    if (propKey === 'appProperty') return TITLE_APP_PROPERTY;
    if (propKey === 'requiredConfiguration')
      return TITLE_REQUIRED_CONFIGURATION;
    return propKey;
  }

  function printValidation(configs: ValidationMappingConfig[]) {
    return configs
      .sort(
        (a, b) =>
          Number(a.isField) - Number(b.isField) ||
          a.status.localeCompare(b.status) ||
          a.shorthand.localeCompare(b.shorthand)
      )
      .map((config) => (
        <Fragment key={`${config.shorthand + config.title}-mapping`}>
          {renderMessages(config)}
          {renderMandatory(config)}
        </Fragment>
      ));
  }

  /**
   * Presents external ID information for a configuration
   */
  function presentExtId(config: ValidationMappingConfig) {
    if (!config.extId) {
      return <b>No External ID</b>;
    }

    const name =
      allFields.find((f) => f.extId === config.extId)?.name ||
      allTypes.find((f) => f.extId === config.extId)?.name ||
      'Unknown';

    return (
      <>
        <b>{name}</b> Ext.id <b>{config.extId}</b>
      </>
    );
  }

  /**
   * Extracts and formats keys from an object as a comma-separated string
   */
  function formatObjectKeys(obj: Record<string, any>): string {
    return Object.keys(obj).join(', ');
  }

  /**
   * Determines if a prop is an object that should have its keys extracted
   */
  function isExtractableObject(prop: any): prop is Record<string, any> {
    return typeof prop === 'object' && prop !== null && !Array.isArray(prop);
  }

  /**
   * Presents a prop value in a user-friendly format
   */
  function presentProp(
    prop: ValidationMappingConfig['props'],
    onlyField: boolean
  ) {
    // Check for predefined app property names
    if (AppPropertiesNames['' + prop]) {
      return AppPropertiesNames['' + prop];
    }

    // Handle objects (like requiredConfiguration) by showing their keys
    if (isExtractableObject(prop)) {
      const keys = formatObjectKeys(prop);
      return `(${keys})`;
    }

    // Get label from constants based on context
    if (onlyField) {
      return _.get(CONSTANTS, `AppPropertyProps.${prop}.label`);
    }

    return (
      _.get(CONSTANTS, `ObjectTypeGroupProps.${prop}.label`) ||
      _.get(CONSTANTS, `AppPropertyProps.${prop}.label`) ||
      `${prop} (missing in @timeedit/types)`
    );
  }

  function presentConfig(config: ValidationMappingConfig) {
    return (
      <Tooltip
        title={'Technical shorthand: ' + config.shorthand}
        key={config.shorthand}
      >
        {Object.entries(config.props)
          .map(([propKey, prop]) => {
            const label = presentProp(prop, config.isField);
            if (_.isUndefined(label)) {
              return undefined;
            }

            const prefix =
              propKey === 'requiredConfiguration'
                ? ` with the ${presentPropKey(propKey)}`
                : config.isField
                  ? TITLE_APP_PROPERTY
                  : TITLE_OBJECT_TYPE_GROUP;

            return (
              <Fragment key={`${propKey}${prop}${config.id}`}>
                {prefix} <b> {label} </b>
              </Fragment>
            );
          })
          .filter(Boolean)}
      </Tooltip>
    );
  }

  function configHelp(config: ValidationMappingConfig) {
    return Object.entries(config.props)
      .map(([propKey, prop]) => {
        const label = presentProp(prop, false);
        if (_.isUndefined(label)) {
          return undefined;
        }

        if (propKey === 'requiredConfiguration') {
          return (
            <>
              &nbsp; with {presentPropKey(propKey)} <b>{label}</b>
            </>
          );
        }

        return (
          <>
            &nbsp;an {presentPropKey(propKey)} to <b>{label}</b>
          </>
        );
      })
      .filter(Boolean);
  }

  function renderMandatory(config: ValidationMappingConfig) {
    if (!config.isField) return;
    if (!config.id) return;
    if (!config.missingMandatory) return;
    return (
      <>
        <Optional>
          Field should be mandatory. {presentConfig(config)} is mapped to the{' '}
          {'appProperty' in config.props ? 'field ' : 'type '}
          {presentExtId(config)} Id <b>{config.id}</b>
        </Optional>
        <Typography.Paragraph>
          <Typography.Link
            href={`/${customerSignature}/configuration/fields?item1=${config.id}`}
            underline
            style={{ marginLeft: '2em' }}
          >
            {`Edit ${FIELD_CONFIGURATION}`}
          </Typography.Link>
        </Typography.Paragraph>
      </>
    );
  }

  function renderMessages(config: ValidationMappingConfig) {
    switch (config.status) {
      case 'valid': {
        return (
          <Success>
            {' '}
            {presentConfig(config)} is mapped to the{' '}
            {'appProperty' in config.props ? 'field ' : 'type '}
            {presentExtId(config)} Id <b>{config.id}</b>.
          </Success>
        );
      }
      case 'deprecated': {
        return (
          <>
            <Danger>
              {presentConfig(config)} is mapped to the{' '}
              {'appProperty' in config.props ? 'field ' : 'type '}
              {presentExtId(config)}, but has no Id.
            </Danger>
            <Suggestion>
              Re-save the mapping. The mapping is old and does not contain id.
            </Suggestion>
          </>
        );
      }
      case 'duplication': {
        return (
          <>
            <Danger>
              Could not validate {presentConfig(config)} because objectTypeGroup{' '}
              <b>
                {' '}
                {_.get(
                  CONSTANTS,
                  `ObjectTypeGroupProps.${config.props.objectTypeGroup}.label`
                )}
              </b>{' '}
              is assigned to multiple types.
            </Danger>
            <Suggestion>Resolve the duplication.</Suggestion>
          </>
        );
      }
      case 'optional': {
        return (
          <>
            <Optional>
              {presentConfig(config)} is not mapped and optional.
            </Optional>
            <Suggestion>Optionally set {configHelp(config)}.</Suggestion>
          </>
        );
      }
      case 'missingRequiredFields': {
        return (
          <>
            <Danger>
              {presentConfig(config)} is missing required configuration.
            </Danger>
            <Suggestion>Set {configHelp(config)}</Suggestion>
            <Typography.Paragraph>
              <Typography.Link
                href={`/${customerSignature}/configuration/fields?item1=${config.id}`}
                underline
                style={{ marginLeft: '2em' }}
              >
                {`Edit ${TITLE_REQUIRED_CONFIGURATION}`}
              </Typography.Link>
            </Typography.Paragraph>
          </>
        );
      }
      default: {
        return (
          <>
            <Danger>{presentConfig(config)} is not mapped.</Danger>
            <Suggestion>Set {configHelp(config)}.</Suggestion>
          </>
        );
      }
    }
  }
}

function Success({ children }: { children: React.ReactNode }) {
  return (
    <Typography.Paragraph>
      <Tag
        icon={<Icon id="CheckCircleOutlined" alt="Check Circle" />}
        color="success"
      >
        {children}
      </Tag>
    </Typography.Paragraph>
  );
}

function Optional({ children }: { children: React.ReactNode }) {
  return (
    <Typography.Paragraph>
      <Tag
        icon={<Icon id="QuestionCircleOutlined" alt="Question Circle" />}
        color="warning"
      >
        {children}
      </Tag>
    </Typography.Paragraph>
  );
}

function Danger({ children }: { children: React.ReactNode }) {
  return (
    <Typography.Paragraph>
      <Tag
        icon={<Icon id="CloseCircleOutlined" alt="Close Circle" />}
        color="error"
      >
        {children}
      </Tag>
    </Typography.Paragraph>
  );
}

function Suggestion({ children }: { children: React.ReactNode }) {
  return (
    <Typography.Paragraph>
      <Tag icon={<Icon id="BulbOutlined" alt="Bulb" />} color="default">
        {children}
      </Tag>
    </Typography.Paragraph>
  );
}

export default MappingValidation;
